/**
 * auth-ui.ts — signing in, accepting an invite, signing out (P2/US-212).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THERE IS NO SIGN-UP VIEW, BECAUSE THERE IS NO ROUTE BEHIND ONE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * D5: an admin invites client users into their organisation. ARCHITECTURE §8.1 sharpens it to "no
 * open sign-up path exists in the API at all", and `apps/api/src/auth.ts` refuses to create a user
 * without a resolved invite — from any path, including better-auth's own. A "creer un compte" form
 * here would be a button that cannot work, which is worse than an absent one: it tells a visitor
 * the product is open to them.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE FIDELITY RULE APPLIES TO NEW SURFACES TOO — SIDEWAYS
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * P1's Track C rule was "no accents added, no labels reworded, no layout modernised" for everything
 * ported from the oracle. Nothing here is ported — the oracle had no accounts — so the rule cannot
 * be applied literally. What it means for a new surface is: **match the existing visual language
 * rather than introducing a second one.** So these views use the tool's own `.card`, `.row`,
 * `label`/`input` structure and its own unaccented French, and they introduce no new palette. A
 * sign-in screen in a different design language would announce that two teams built this.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * ACCEPTING AN INVITE IS TWO REQUESTS, ON PURPOSE
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `POST /v1/org/invites/accept` creates the account and does **not** sign it in (`autoSignIn` is
 * off server-side). The controller below follows it with a sign-in using the password just chosen.
 * That exercises the password immediately, while the user still expects to be typing one — rather
 * than at their next visit, when a typo has become a support request.
 */

import type { AcceptInviteResponse, MeResponse } from '@ronceray/contract';

import { failureMessage, type ApiFailureKind, type ApiResult } from './api-client';
import { escapeHtml } from './dom';

/** The shortest password the contract will accept. Stated here so the hint cannot drift from it. */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * What went wrong signing in, in French, unaccented like the rest of the tool.
 *
 * A wrong password and an unreachable server get **different sentences**, for the reason P1 gave
 * for `failureMessage`: they call for completely different actions, and one generic message leaves
 * a user retyping a password at a server that is down.
 */
export function signInFailureMessage(kind: ApiFailureKind): string {
  // 401 from the sign-in endpoint itself does not mean "your session expired"; it means the
  // credentials were refused. Same status, different sentence, because this is the one route where
  // being unauthenticated is the expected starting state.
  if (kind === 'auth' || kind === 'unknown' || kind === 'invalid') {
    return 'Adresse ou mot de passe incorrect.';
  }
  return failureMessage(kind);
}

/** What went wrong accepting an invite. A dead link is the common case and says so. */
export function inviteFailureMessage(kind: ApiFailureKind): string {
  if (kind === 'unknown' || kind === 'auth') {
    return "Cette invitation n'est plus valable. Demandez-en une nouvelle a votre administrateur.";
  }
  if (kind === 'invalid') {
    return `Mot de passe trop court : ${MIN_PASSWORD_LENGTH} caracteres au minimum.`;
  }
  return failureMessage(kind);
}

/**
 * The sign-in view.
 *
 * `type="password"` and `autocomplete` attributes are present so a password manager behaves; an
 * engineer signing into a tool they use daily should not be typing a passphrase by hand.
 */
export function signInHTML(message?: string): string {
  return `<form class="card p2-auth" id="p2-signin-form" onsubmit="return false">
<h2>Connexion</h2>
<p class="hint">Acces reserve aux utilisateurs invites par leur administrateur.</p>
<div class="row"><label for="p2-email">Adresse electronique</label>
<input type="email" id="p2-email" autocomplete="username" required></div>
<div class="row"><label for="p2-password">Mot de passe</label>
<input type="password" id="p2-password" autocomplete="current-password" required></div>
<div class="tools"><button type="submit" id="p2-signin">Se connecter</button></div>
${messageBlock(message)}</form>`;
}

/**
 * The invite-acceptance view.
 *
 * The token is **not** shown and not editable. It arrived in the URL; putting it in a field would
 * invite someone to correct it, and a mistyped single-use token is indistinguishable from an
 * expired one.
 */
export function inviteHTML(message?: string): string {
  return `<form class="card p2-auth" id="p2-invite-form" onsubmit="return false">
<h2>Creer votre acces</h2>
<p class="hint">Choisissez un mot de passe de ${MIN_PASSWORD_LENGTH} caracteres au minimum.</p>
<div class="row"><label for="p2-name">Nom</label>
<input type="text" id="p2-name" autocomplete="name" required></div>
<div class="row"><label for="p2-invite-password">Mot de passe</label>
<input type="password" id="p2-invite-password" autocomplete="new-password" required></div>
<div class="tools"><button type="submit" id="p2-accept">Valider</button></div>
${messageBlock(message)}</form>`;
}

function messageBlock(message?: string): string {
  return message === undefined || message === ''
    ? '<p class="p2-msg" id="p2-authmsg" hidden></p>'
    : `<p class="p2-msg" id="p2-authmsg">${escapeHtml(message)}</p>`;
}

/**
 * The signed-in identity strip, for the header.
 *
 * It names the **organisation**, not only the person. In a multi-tenant tool where every list is
 * silently filtered by tenancy, "which organisation am I looking at" is the question that makes an
 * empty project list legible rather than alarming.
 */
export function identityHTML(me: MeResponse): string {
  const who = me.name !== '' ? me.name : me.email;
  return `<span class="p2-who" title="${escapeHtml(me.email)}">${escapeHtml(who)} &mdash; ${escapeHtml(me.orgName)}</span>
<button id="p2-signout" type="button">Deconnexion</button>`;
}

/* ================================================================================================
 * The controller
 * ============================================================================================== */

export interface AuthDeps {
  readonly signIn: (email: string, password: string) => Promise<ApiResult<unknown>>;
  readonly acceptInvite: (
    token: string,
    name: string,
    password: string,
  ) => Promise<ApiResult<AcceptInviteResponse>>;
}

/**
 * Wire whichever auth view is on screen.
 *
 * DOM-binding, so it is covered by the Playwright suite rather than by a simulated document — the
 * same split P1 used for `optimisePanel`. The pure pieces above are what the unit suite asserts.
 */
export function authView(
  host: HTMLElement,
  deps: AuthDeps,
  onSignedIn: () => void | Promise<void>,
) {
  function setMessage(text: string): void {
    const box = host.querySelector<HTMLElement>('#p2-authmsg');
    if (box === null) return;
    box.textContent = text;
    box.hidden = text === '';
  }

  function busy(button: HTMLButtonElement | null, on: boolean): void {
    if (button === null) return;
    button.disabled = on;
  }

  return {
    /** Render and wire the sign-in form. */
    showSignIn(message?: string): void {
      host.innerHTML = signInHTML(message);

      const form = host.querySelector<HTMLFormElement>('#p2-signin-form');
      const button = host.querySelector<HTMLButtonElement>('#p2-signin');

      form?.addEventListener('submit', () => {
        const email = host.querySelector<HTMLInputElement>('#p2-email')?.value ?? '';
        const password = host.querySelector<HTMLInputElement>('#p2-password')?.value ?? '';

        setMessage('');
        busy(button, true);

        void deps.signIn(email, password).then(async (result) => {
          busy(button, false);
          if (!result.ok) {
            setMessage(signInFailureMessage(result.kind));
            return;
          }
          await onSignedIn();
        });
      });

      host.querySelector<HTMLInputElement>('#p2-email')?.focus();
    },

    /** Render and wire the invite form for a token taken from the URL. */
    showInvite(token: string): void {
      host.innerHTML = inviteHTML();

      const form = host.querySelector<HTMLFormElement>('#p2-invite-form');
      const button = host.querySelector<HTMLButtonElement>('#p2-accept');

      form?.addEventListener('submit', () => {
        const name = host.querySelector<HTMLInputElement>('#p2-name')?.value ?? '';
        const password = host.querySelector<HTMLInputElement>('#p2-invite-password')?.value ?? '';

        setMessage('');
        busy(button, true);

        void deps.acceptInvite(token, name, password).then(async (accepted) => {
          if (!accepted.ok) {
            busy(button, false);
            setMessage(inviteFailureMessage(accepted.kind));
            return;
          }

          // Straight into a sign-in with the password just chosen, at the address the ACCOUNT was
          // created with — which comes back in the response, because the account takes its address
          // from the invite row and the browser has no other way to know it. See the file header.
          const signedIn = await deps.signIn(accepted.value.email, password).catch(() => undefined);
          busy(button, false);

          if (signedIn === undefined || !signedIn.ok) {
            // The account exists; only the automatic sign-in did not happen. Say exactly that,
            // because "votre compte est cree" followed by a sign-in form is otherwise baffling.
            setMessage('Compte cree. Connectez-vous avec votre adresse et ce mot de passe.');
            return;
          }
          await onSignedIn();
        });
      });

      host.querySelector<HTMLInputElement>('#p2-name')?.focus();
    },

    setMessage,
  };
}
